import React, { useEffect, useMemo, useState } from 'react';
import Layout from '../components/Layout';
import { api } from '../services/api';
import RobotsBlockedAgents, { RobotsBlockedAgent } from '../components/RobotsBlockedAgents';
import BlockReasonPopover, { BlockReason } from '../components/BlockReasonPopover';

interface ClientRow {
  id: string;
  name: string;
  website: string | null;
  industry?: string | null;
  is_bh?: boolean;
  saved?: SavedResult | null;
}

interface SavedResult {
  result: SiteResult | null;
  allowed: number;
  blocked: number;
  errors: number;
  total: number;
  homepage_status: string | null;
  checked_at: string | null;
}

interface BotMeta {
  id: string;
  name: string;
  vendor: string;
  purpose: string;
  robots_token: string;
  checks_http: boolean;
}

type BotStatus = 'allowed' | 'blocked' | 'partial' | 'robots_only' | 'error' | 'inconclusive';

interface BotResult {
  id: string;
  name: string;
  vendor: string;
  purpose: string;
  robots_token: string;
  robots_blocked: boolean | null;
  http_status: number | null;
  http_blocked: boolean;
  http_inconclusive?: boolean;
  meta_blocked?: boolean;
  meta_reason?: string | null;
  error: string | null;
  status: BotStatus;
  evidence: string;
  impact?: string | null;
  block_reasons?: BlockReason[] | null;
}

interface SiteResult {
  url: string;
  robots: { fetched: boolean; status: number | null; error: string | null; blocked_agents?: RobotsBlockedAgent[] };
  homepage?: {
    status_code: number | null;
    ok: boolean;
    hard_blocked: boolean;
    error: string | null;
    final_url: string | null;
    prober_ip_blocked?: boolean;
    warp_retry_used?: boolean;
    direct_prober_blocked?: boolean;
    server?: string | null;
    meta_robots?: { name: string; content: string }[];
    x_robots_tag?: string | null;
  };
  results: BotResult[];
}

interface ClientReport {
  client: ClientRow;
  result?: SiteResult;
  skipped?: boolean;
  reason?: string;
}

const STATUS_STYLES: Record<BotStatus, { label: string; cls: string; dot: string }> = {
  allowed: {
    label: 'Allowed',
    cls: 'bg-emerald-50 text-emerald-700 border-emerald-200',
    dot: 'bg-emerald-500',
  },
  blocked: {
    label: 'Blocked',
    cls: 'bg-rose-50 text-rose-700 border-rose-200',
    dot: 'bg-rose-500',
  },
  partial: {
    label: 'Partially blocked',
    cls: 'bg-orange-50 text-orange-700 border-orange-200',
    dot: 'bg-orange-500',
  },
  robots_only: {
    label: 'Robots OK',
    cls: 'bg-sky-50 text-sky-700 border-sky-200',
    dot: 'bg-sky-500',
  },
  error: {
    label: 'Unreachable',
    cls: 'bg-amber-50 text-amber-700 border-amber-200',
    dot: 'bg-amber-500',
  },
  inconclusive: {
    label: 'Inconclusive',
    cls: 'bg-amber-50 text-amber-700 border-amber-200',
    dot: 'bg-amber-500',
  },
};

export default function LLMBlockTestPage() {
  const [clients, setClients] = useState<ClientRow[]>([]);
  const [bots, setBots] = useState<BotMeta[]>([]);
  const [reports, setReports] = useState<Record<string, ClientReport>>({});
  const [loadingClients, setLoadingClients] = useState(false);
  const [running, setRunning] = useState(false);
  const [singleUrl, setSingleUrl] = useState('');
  const [singleResult, setSingleResult] = useState<SiteResult | null>(null);
  const [singleLoading, setSingleLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [syncInfo, setSyncInfo] = useState<{ active: number; added: string[]; removed: string[] } | null>(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [statusFilter, setStatusFilter] = useState<'all' | 'with-blocks' | 'no-data'>('all');
  const [selectedClientId, setSelectedClientId] = useState<string | null>(null);
  const [shareModal, setShareModal] = useState<{
    open: boolean;
    title: string;
    creating: boolean;
    url: string | null;
    error: string | null;
  }>({ open: false, title: '', creating: false, url: null, error: null });

  interface ShareRow {
    id: number;
    share_token: string;
    title: string | null;
    url_path: string;
    client_count: number;
    is_active: boolean;
    view_count: number;
    created_at: string | null;
  }
  const [shares, setShares] = useState<ShareRow[]>([]);
  const [sharesLoading, setSharesLoading] = useState(false);
  const [revokingToken, setRevokingToken] = useState<string | null>(null);
  const [copiedToken, setCopiedToken] = useState<string | null>(null);

  const loadShares = async () => {
    try {
      setSharesLoading(true);
      const r = await api.get('/llm-block-test/shares');
      setShares(r.data?.shares || []);
    } catch {
      // non-fatal
    } finally {
      setSharesLoading(false);
    }
  };

  useEffect(() => { loadShares(); }, []);

  const handleRevokeShare = async (s: ShareRow) => {
    if (!window.confirm(
      `Delete this report${s.title ? ` ("${s.title}")` : ''}?\n\n` +
      `The public URL will stop working immediately. Anyone who already has the link will see "Report unavailable".`,
    )) return;
    setRevokingToken(s.share_token);
    try {
      await api.delete(`/llm-block-test/shares/${s.share_token}`);
      await loadShares();
    } catch (e: any) {
      setError(e?.response?.data?.detail || e?.message || 'Failed to delete share');
    } finally {
      setRevokingToken(null);
    }
  };

  const copyShareUrl = (token: string) => {
    const full = `${window.location.origin}/llm-block-test/report/${token}`;
    navigator.clipboard.writeText(full);
    setCopiedToken(token);
    setTimeout(() => setCopiedToken((t) => (t === token ? null : t)), 1500);
  };

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        setLoadingClients(true);
        const [c, b] = await Promise.all([
          api.get('/llm-block-test/clients'),
          api.get('/llm-block-test/bots'),
        ]);
        if (cancelled) return;
        const clientList: ClientRow[] = c.data?.clients || [];
        setClients(clientList);
        setBots(b.data?.bots || []);
        // Hydrate the in-memory `reports` map from any saved results so the
        // page comes up pre-populated and the user only needs to re-check
        // rows they care about.
        const initial: Record<string, ClientReport> = {};
        for (const cl of clientList) {
          if (cl.saved && cl.saved.result) {
            initial[cl.id] = {
              client: { id: cl.id, name: cl.name, website: cl.website },
              result: cl.saved.result,
            };
          }
        }
        setReports(initial);
      } catch (e: any) {
        setError(e?.response?.data?.detail || e?.message || 'Failed to load clients');
      } finally {
        setLoadingClients(false);
      }
    })();
    return () => { cancelled = true; };
  }, []);

  const filteredClients = useMemo(() => {
    const q = searchQuery.trim().toLowerCase();
    return clients.filter((c) => {
      if (q && !c.name.toLowerCase().includes(q) && !(c.website || '').toLowerCase().includes(q)) {
        return false;
      }
      if (statusFilter === 'no-data' && c.website) return false;
      if (statusFilter === 'with-blocks') {
        const r = reports[c.id]?.result;
        if (!r) return false;
        if (!r.results.some((x) => x.status === 'blocked' || x.status === 'partial') && !r.robots.blocked_agents?.length) return false;
      }
      return true;
    });
  }, [clients, searchQuery, statusFilter, reports]);

  const runAll = async () => {
    setRunning(true);
    setError(null);
    setSyncInfo(null);
    setReports({});
    try {
      const token = localStorage.getItem('auth_token') || '';
      const resp = await fetch('/api/llm-block-test/check-batch', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...(token ? { Authorization: `Bearer ${token}` } : {}),
        },
        body: JSON.stringify({}),
      });
      if (!resp.ok || !resp.body) {
        const detail = await resp.json().then((j) => j?.detail).catch(() => null);
        throw new Error(detail || `HTTP ${resp.status}`);
      }
      const reader = resp.body.getReader();
      const decoder = new TextDecoder();
      let buffer = '';
      while (true) {
        const { value, done } = await reader.read();
        if (done) break;
        buffer += decoder.decode(value, { stream: true });
        const parts = buffer.split('\n');
        buffer = parts.pop() || '';
        for (const raw of parts) {
          const line = raw.trim();
          if (!line) continue;
          try {
            const evt = JSON.parse(line);
            if (evt.done) continue;
            if (evt.sync) {
              // Fresh active-client list from Notion — replaces the list shown
              // so added clients appear and inactive ones drop out.
              setClients((evt.sync.clients || []).map((c: ClientRow) => ({ ...c, saved: null })));
              setSyncInfo({
                active: (evt.sync.clients || []).length,
                added: evt.sync.added || [],
                removed: evt.sync.removed || [],
              });
              continue;
            }
            if (evt.client) {
              setReports((prev) => ({
                ...prev,
                [evt.client.id]: {
                  client: evt.client,
                  result: evt.result,
                  skipped: evt.skipped,
                  reason: evt.reason,
                },
              }));
            }
          } catch {
            // ignore
          }
        }
      }
    } catch (e: any) {
      setError(e?.message || 'Batch run failed');
    } finally {
      setRunning(false);
    }
  };

  const checkSingle = async () => {
    const u = singleUrl.trim();
    if (!u) return;
    setSingleLoading(true);
    setError(null);
    try {
      const r = await api.post('/llm-block-test/check', { url: u });
      setSingleResult(r.data);
    } catch (e: any) {
      setError(e?.response?.data?.detail || e?.message || 'Check failed');
    } finally {
      setSingleLoading(false);
    }
  };

  const recheckClient = async (c: ClientRow) => {
    if (!c.website) return;
    setReports((prev) => ({
      ...prev,
      [c.id]: { client: c, result: undefined },
    }));
    try {
      const r = await api.post('/llm-block-test/check', {
        url: c.website,
        name: c.name,
        client_id: c.id,
        industry: c.industry,
        is_bh: c.is_bh,
      });
      setReports((prev) => ({
        ...prev,
        [c.id]: { client: c, result: r.data },
      }));
    } catch (e: any) {
      setReports((prev) => ({
        ...prev,
        [c.id]: {
          client: c,
          result: {
            url: c.website || '',
            robots: { fetched: false, status: null, error: e?.message || 'failed' },
            results: [],
          },
        },
      }));
    }
  };

  const blockedCount = (r?: SiteResult) =>
    r ? r.results.filter((x) => x.status === 'blocked' || x.status === 'partial').length : 0;
  const allowedCount = (r?: SiteResult) =>
    r ? r.results.filter((x) => x.status === 'allowed' || x.status === 'robots_only').length : 0;
  const errorCount = (r?: SiteResult) =>
    r ? r.results.filter((x) => x.status === 'error').length : 0;
  const totalCount = (r?: SiteResult) => (r ? r.results.length : 0);

  const totalChecked = Object.values(reports).filter((r) => r.result).length;
  const totalBlocking = Object.values(reports).filter(
    (r) => (r.result?.results || []).some((x) => x.status === 'blocked' || x.status === 'partial'),
  ).length;
  const totalMissing = clients.filter((c) => !c.website).length;

  const detailReport = selectedClientId ? reports[selectedClientId] : null;

  return (
    <Layout>
      <div className="space-y-6">
        <div className="flex items-start justify-between gap-4">
          <div>
            <h1 className="text-2xl font-bold text-gray-900">LLM Block Test</h1>
            <p className="text-sm text-gray-600 mt-1 max-w-3xl">
              Pulls every client website from Notion and checks it against 10 AI assistants —
              ChatGPT, Claude, Gemini, Copilot, Perplexity, DeepSeek, Mistral, Grok, Meta AI and
              Poe — using every crawler each one sends, to flag whether the site allows or blocks
              it at the robots.txt, homepage or server (WAF) layer. "Partially blocked" means some
              of that assistant's crawlers are blocked.
            </p>
          </div>
          <div className="flex items-center gap-2">
            <button
              onClick={() => setShareModal({ open: true, title: '', creating: false, url: null, error: null })}
              disabled={Object.keys(reports).length === 0}
              className="px-4 py-2 bg-white border border-gray-300 text-gray-700 text-sm font-medium rounded-lg hover:bg-gray-50 disabled:opacity-50"
              title={Object.keys(reports).length === 0 ? 'Run a check first' : 'Generate a public share link'}
            >
              Share report
            </button>
            <button
              onClick={runAll}
              disabled={running || loadingClients}
              className="px-4 py-2 bg-brand-600 text-white text-sm font-medium rounded-lg hover:bg-brand-700 disabled:opacity-50"
            >
              {running ? 'Checking…' : `Run all ${clients.length} clients`}
            </button>
          </div>
        </div>

        {syncInfo && (
          <div className="bg-sky-50 border border-sky-200 text-sky-900 px-4 py-3 rounded-lg text-sm">
            Client list synced from Notion: {syncInfo.active} active clients.
            {syncInfo.added.length > 0 && <> New: {syncInfo.added.join(', ')}.</>}
            {syncInfo.removed.length > 0 && <> Removed (no longer active): {syncInfo.removed.join(', ')}.</>}
            {syncInfo.added.length === 0 && syncInfo.removed.length === 0 && <> No changes.</>}
          </div>
        )}

        {error && (
          <div className="bg-rose-50 border border-rose-200 text-rose-700 px-4 py-3 rounded-lg text-sm">
            {error}
          </div>
        )}

        {/* One-off URL probe */}
        <div className="bg-white border border-gray-200 rounded-lg p-4">
          <div className="flex items-center gap-2 mb-3">
            <h2 className="text-sm font-semibold text-gray-800">Quick probe</h2>
            <span className="text-xs text-gray-500">— test any URL without needing a Notion entry</span>
          </div>
          <div className="flex gap-2">
            <input
              type="text"
              value={singleUrl}
              onChange={(e) => setSingleUrl(e.target.value)}
              onKeyDown={(e) => { if (e.key === 'Enter') checkSingle(); }}
              placeholder="https://example.com"
              className="flex-1 px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-brand-500"
            />
            <button
              onClick={checkSingle}
              disabled={singleLoading || !singleUrl.trim()}
              className="px-4 py-2 bg-gray-800 text-white text-sm font-medium rounded-lg hover:bg-gray-900 disabled:opacity-50"
            >
              {singleLoading ? 'Checking…' : 'Check'}
            </button>
          </div>
          {singleResult && (
            <div className="mt-4">
              <BotMatrix result={singleResult} />
            </div>
          )}
        </div>

        {/* Stats */}
        <div className="grid grid-cols-1 sm:grid-cols-4 gap-3">
          <StatCard label="Clients in Notion" value={clients.length} loading={loadingClients} />
          <StatCard label="Sites scanned" value={totalChecked} />
          <StatCard label="Sites blocking ≥1 LLM" value={totalBlocking} tone="warn" />
          <StatCard label="Missing website URL" value={totalMissing} tone="muted" />
        </div>

        {/* Bot legend */}
        {bots.length > 0 && (
          <div className="bg-white border border-gray-200 rounded-lg p-4">
            <h3 className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-3">
              Bots checked
            </h3>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-2">
              {bots.map((b) => (
                <div key={b.id} className="text-xs border border-gray-200 rounded-md px-3 py-2">
                  <div className="font-medium text-gray-900">{b.name}</div>
                  <div className="text-gray-500">{b.vendor} · {b.purpose}</div>
                  <div className="text-gray-400 mt-1 font-mono">{b.robots_token}</div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Filter bar */}
        <div className="flex flex-wrap items-center gap-2">
          <input
            type="text"
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            placeholder="Filter clients…"
            className="px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-brand-500 w-64"
          />
          <select
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value as any)}
            className="px-3 py-2 border border-gray-300 rounded-lg text-sm bg-white"
          >
            <option value="all">All clients</option>
            <option value="with-blocks">Only sites blocking ≥1 LLM</option>
            <option value="no-data">Missing website URL</option>
          </select>
          <span className="text-xs text-gray-500">{filteredClients.length} shown</span>
        </div>

        {/* Client list */}
        <div className="bg-white border border-gray-200 rounded-lg overflow-hidden">
          <table className="w-full text-sm">
            <thead className="bg-gray-50 border-b border-gray-200">
              <tr>
                <th className="text-left px-4 py-2 font-medium text-gray-600">Client</th>
                <th className="text-left px-4 py-2 font-medium text-gray-600">Website</th>
                <th className="text-left px-4 py-2 font-medium text-gray-600">Homepage</th>
                <th className="text-left px-4 py-2 font-medium text-gray-600">Allowed</th>
                <th className="text-left px-4 py-2 font-medium text-gray-600">Blocked</th>
                <th className="text-left px-4 py-2 font-medium text-gray-600">Errors</th>
                <th className="text-left px-4 py-2 font-medium text-gray-600">Robots</th>
                <th className="text-right px-4 py-2 font-medium text-gray-600">Action</th>
              </tr>
            </thead>
            <tbody>
              {filteredClients.map((c, idx) => {
                const rep = reports[c.id];
                const r = rep?.result;
                const prev = idx > 0 ? filteredClients[idx - 1] : null;
                const showBhHeader = idx === 0 && c.is_bh;
                const showNonBhDivider = !c.is_bh && (prev?.is_bh || idx === 0);
                return (
                  <React.Fragment key={c.id}>
                    {showBhHeader && (
                      <tr className="bg-emerald-50/60">
                        <td colSpan={8} className="px-4 py-2 text-xs font-semibold text-emerald-700 uppercase tracking-wider">
                          Behavioral Health · {filteredClients.filter((x) => x.is_bh).length}
                        </td>
                      </tr>
                    )}
                    {showNonBhDivider && (
                      <tr className="bg-slate-100">
                        <td colSpan={8} className="px-4 py-2 text-xs font-semibold text-slate-600 uppercase tracking-wider">
                          Other industries · {filteredClients.filter((x) => !x.is_bh).length}
                        </td>
                      </tr>
                    )}
                  <tr className="border-b border-gray-100 hover:bg-gray-50">
                    <td className="px-4 py-3 font-medium text-gray-900">{c.name}</td>
                    <td className="px-4 py-3 text-gray-700">
                      {c.website ? (
                        <a href={c.website} target="_blank" rel="noopener noreferrer"
                          className="text-brand-600 hover:underline truncate inline-block max-w-[280px] align-bottom">
                          {c.website.replace(/^https?:\/\//, '').replace(/\/$/, '')}
                        </a>
                      ) : (
                        <span className="text-amber-600 italic">No URL in Notion</span>
                      )}
                    </td>
                    <td className="px-4 py-3">
                      {r?.homepage ? (
                        r.homepage.prober_ip_blocked ? (
                          <span className="inline-flex items-center gap-1.5 px-2 py-0.5 rounded-full border bg-amber-50 text-amber-700 border-amber-200 text-xs font-medium"
                                title={`Site returned HTTP ${r.homepage.status_code} to a normal browser even via WARP fallback. Our prober's IP is being blocked at the WAF/CDN layer and WARP didn't bypass it — bot HTTP probes below are inconclusive. The verdict relies on robots.txt + homepage meta tags only for this site.`}>
                            <span className="w-1.5 h-1.5 rounded-full bg-amber-500" />
                            our IP blocked ({r.homepage.status_code})
                          </span>
                        ) : r.homepage.warp_retry_used ? (
                          <span className="inline-flex items-center gap-1.5 px-2 py-0.5 rounded-full border bg-sky-50 text-sky-700 border-sky-200 text-xs font-medium"
                                title={`Direct probe got HTTP ${(r.homepage as any).direct_prober_blocked ? 'blocked' : '?'} so we retried via Cloudflare WARP and got HTTP ${r.homepage.status_code}. Bot probes ran through WARP too — verdict is fully conclusive.`}>
                            <span className="w-1.5 h-1.5 rounded-full bg-sky-500" />
                            {r.homepage.status_code} via WARP
                          </span>
                        ) : r.homepage.ok ? (
                          <span className="inline-flex items-center gap-1.5 px-2 py-0.5 rounded-full border bg-emerald-50 text-emerald-700 border-emerald-200 text-xs font-medium">
                            <span className="w-1.5 h-1.5 rounded-full bg-emerald-500" />
                            {r.homepage.status_code} OK
                          </span>
                        ) : r.homepage.error ? (
                          <span className="inline-flex items-center gap-1.5 px-2 py-0.5 rounded-full border bg-amber-50 text-amber-700 border-amber-200 text-xs font-medium" title={r.homepage.error}>
                            <span className="w-1.5 h-1.5 rounded-full bg-amber-500" />
                            unreachable
                          </span>
                        ) : (
                          <span className="inline-flex items-center gap-1.5 px-2 py-0.5 rounded-full border bg-rose-50 text-rose-700 border-rose-200 text-xs font-medium">
                            <span className="w-1.5 h-1.5 rounded-full bg-rose-500" />
                            {r.homepage.status_code ?? '?'}
                          </span>
                        )
                      ) : '—'}
                    </td>
                    <td className="px-4 py-3 font-medium">
                      {r ? (
                        <span className={allowedCount(r) === totalCount(r) ? 'text-emerald-700' : 'text-gray-700'}>
                          {allowedCount(r)}/{totalCount(r)}
                        </span>
                      ) : '—'}
                    </td>
                    <td className="px-4 py-3 font-medium">
                      {r ? (
                        <span className={blockedCount(r) > 0 ? 'text-rose-700' : 'text-gray-500'}>
                          {blockedCount(r)}/{totalCount(r)}
                        </span>
                      ) : '—'}
                    </td>
                    <td className="px-4 py-3 font-medium">
                      {r ? (
                        <span className={errorCount(r) > 0 ? 'text-amber-700' : 'text-gray-500'}>
                          {errorCount(r)}/{totalCount(r)}
                        </span>
                      ) : '—'}
                    </td>
                    <td className="px-4 py-3 text-xs text-gray-500">
                      {r ? (r.robots.fetched
                          ? (r.robots.blocked_agents?.length
                              ? <span className="text-rose-700 font-medium"
                                      title={r.robots.blocked_agents.map((a) => a.assistant ? `${a.agent} (${a.assistant})` : a.agent).join(', ')}>
                                  {r.robots.blocked_agents.some((a) => a.all_bots)
                                    ? 'blocks all bots'
                                    : `blocks ${r.robots.blocked_agents.length} AI crawler${r.robots.blocked_agents.length === 1 ? '' : 's'}`}
                                </span>
                              : `HTTP ${r.robots.status ?? '?'}`)
                          : (r.robots.error || 'unreachable'))
                        : (rep?.skipped ? 'skipped' : (running ? 'pending…' : 'not run'))}
                    </td>
                    <td className="px-4 py-3 text-right">
                      <div className="flex justify-end gap-2">
                        {r && (
                          <button
                            onClick={() => setSelectedClientId(c.id)}
                            className="px-3 py-1 text-xs text-brand-700 hover:bg-brand-50 rounded"
                          >
                            View
                          </button>
                        )}
                        <button
                          onClick={() => recheckClient(c)}
                          disabled={!c.website || running}
                          className="px-3 py-1 text-xs text-gray-700 border border-gray-300 hover:bg-gray-100 rounded disabled:opacity-50"
                        >
                          {r ? 'Re-check' : 'Check'}
                        </button>
                      </div>
                    </td>
                  </tr>
                  </React.Fragment>
                );
              })}
              {!loadingClients && filteredClients.length === 0 && (
                <tr>
                  <td colSpan={8} className="px-4 py-12 text-center text-gray-500">
                    No clients match.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>

        {/* Share modal */}
        {shareModal.open && (
          <div className="fixed inset-0 z-50 bg-black/40 flex items-center justify-center p-4"
               onClick={() => setShareModal({ ...shareModal, open: false })}>
            <div className="bg-white rounded-lg shadow-xl max-w-md w-full p-5 space-y-4"
                 onClick={(e) => e.stopPropagation()}>
              <div>
                <h3 className="text-lg font-semibold text-gray-900">Share LLM Block Test report</h3>
                <p className="text-xs text-gray-500 mt-1">
                  Generates a public link anyone can open — no login required. The
                  report is a frozen snapshot of your saved results from this run.
                </p>
              </div>
              {shareModal.url ? (
                <>
                  <div className="text-sm text-gray-700">Share this link:</div>
                  <div className="flex gap-2">
                    <input
                      readOnly
                      value={shareModal.url}
                      className="flex-1 px-3 py-2 border border-gray-300 rounded-lg text-xs font-mono bg-gray-50"
                      onFocus={(e) => e.currentTarget.select()}
                    />
                    <button
                      onClick={() => {
                        navigator.clipboard.writeText(shareModal.url || '');
                      }}
                      className="px-3 py-2 bg-brand-600 text-white text-sm rounded-lg hover:bg-brand-700"
                    >
                      Copy
                    </button>
                  </div>
                  <div className="flex justify-end gap-2 pt-2">
                    <a
                      href={shareModal.url}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="px-4 py-2 text-sm text-brand-700 hover:bg-brand-50 rounded-lg"
                    >
                      Open report
                    </a>
                    <button
                      onClick={() => setShareModal({ ...shareModal, open: false })}
                      className="px-4 py-2 text-sm bg-gray-800 text-white rounded-lg hover:bg-gray-900"
                    >
                      Done
                    </button>
                  </div>
                </>
              ) : (
                <>
                  <label className="block">
                    <span className="text-sm text-gray-700">Title (optional)</span>
                    <input
                      type="text"
                      value={shareModal.title}
                      onChange={(e) => setShareModal({ ...shareModal, title: e.target.value })}
                      placeholder="LLM Block Test — Q2 2026"
                      className="mt-1 w-full px-3 py-2 border border-gray-300 rounded-lg text-sm"
                    />
                  </label>
                  {shareModal.error && (
                    <div className="bg-rose-50 border border-rose-200 text-rose-700 px-3 py-2 rounded text-sm">
                      {shareModal.error}
                    </div>
                  )}
                  <div className="flex justify-end gap-2">
                    <button
                      onClick={() => setShareModal({ ...shareModal, open: false })}
                      className="px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 rounded-lg"
                    >
                      Cancel
                    </button>
                    <button
                      onClick={async () => {
                        setShareModal({ ...shareModal, creating: true, error: null });
                        try {
                          const r = await api.post('/llm-block-test/shares', {
                            title: shareModal.title.trim() || null,
                          });
                          const url = `${window.location.origin}${r.data.url_path}`;
                          setShareModal({ ...shareModal, creating: false, url });
                          loadShares();
                        } catch (e: any) {
                          setShareModal({
                            ...shareModal,
                            creating: false,
                            error: e?.response?.data?.detail || e?.message || 'Failed to create share',
                          });
                        }
                      }}
                      disabled={shareModal.creating}
                      className="px-4 py-2 text-sm bg-brand-600 text-white rounded-lg hover:bg-brand-700 disabled:opacity-50"
                    >
                      {shareModal.creating ? 'Creating…' : 'Create link'}
                    </button>
                  </div>
                </>
              )}
            </div>
          </div>
        )}

        {/* Created reports */}
        <div className="space-y-2">
          <div className="flex items-center justify-between">
            <div>
              <h2 className="text-sm font-semibold text-gray-800">Created reports</h2>
              <p className="text-xs text-gray-500">
                Public share links that already exist. Inactive clients are filtered out at view time, so old links stay accurate.
              </p>
            </div>
            <button
              onClick={loadShares}
              disabled={sharesLoading}
              className="px-3 py-1 text-xs text-gray-700 border border-gray-300 hover:bg-gray-100 rounded disabled:opacity-50"
            >
              {sharesLoading ? 'Loading…' : 'Refresh'}
            </button>
          </div>
          <div className="bg-white border border-gray-200 rounded-lg overflow-hidden">
            <table className="w-full text-sm">
              <thead className="bg-gray-50 border-b border-gray-200">
                <tr>
                  <th className="text-left px-3 py-2 font-medium text-gray-600">Created</th>
                  <th className="text-left px-3 py-2 font-medium text-gray-600">Title</th>
                  <th className="text-left px-3 py-2 font-medium text-gray-600">Clients</th>
                  <th className="text-left px-3 py-2 font-medium text-gray-600">Views</th>
                  <th className="text-left px-3 py-2 font-medium text-gray-600">Link</th>
                  <th className="text-right px-3 py-2 font-medium text-gray-600">Action</th>
                </tr>
              </thead>
              <tbody>
                {shares.map((s) => {
                  const dt = s.created_at ? new Date(s.created_at).toLocaleString() : '—';
                  const fullUrl = `${window.location.origin}${s.url_path}`;
                  return (
                    <tr key={s.id} className="border-b border-gray-100">
                      <td className="px-3 py-2 text-xs text-gray-600 whitespace-nowrap">{dt}</td>
                      <td className="px-3 py-2 text-gray-800">
                        {s.title || <span className="text-gray-400 italic">Untitled</span>}
                      </td>
                      <td className="px-3 py-2 text-gray-700">{s.client_count}</td>
                      <td className="px-3 py-2 text-gray-700">{s.view_count}</td>
                      <td className="px-3 py-2">
                        <div className="flex items-center gap-2">
                          <a
                            href={fullUrl}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="text-xs text-brand-600 hover:underline truncate inline-block max-w-[260px] align-bottom font-mono"
                            title={fullUrl}
                          >
                            {`/llm-block-test/report/${s.share_token}`}
                          </a>
                          <button
                            onClick={() => copyShareUrl(s.share_token)}
                            className="text-xs text-gray-500 hover:text-gray-700 px-1.5 py-0.5 border border-gray-200 rounded"
                          >
                            {copiedToken === s.share_token ? 'Copied' : 'Copy'}
                          </button>
                        </div>
                      </td>
                      <td className="px-3 py-2 text-right">
                        <button
                          onClick={() => handleRevokeShare(s)}
                          disabled={revokingToken === s.share_token}
                          className="px-3 py-1 text-xs text-rose-700 border border-rose-200 hover:bg-rose-50 rounded disabled:opacity-50"
                        >
                          {revokingToken === s.share_token ? 'Deleting…' : 'Delete'}
                        </button>
                      </td>
                    </tr>
                  );
                })}
                {shares.length === 0 && !sharesLoading && (
                  <tr>
                    <td colSpan={6} className="px-3 py-8 text-center text-gray-500 text-sm">
                      No reports created yet. Run a check, then click <strong>Share report</strong>.
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </div>

        {/* Detail drawer */}
        {detailReport && detailReport.result && (
          <div className="fixed inset-0 z-50 bg-black/40 flex items-center justify-center p-4"
               onClick={() => setSelectedClientId(null)}>
            <div className="bg-white rounded-lg shadow-xl max-w-4xl w-full max-h-[85vh] overflow-y-auto"
                 onClick={(e) => e.stopPropagation()}>
              <div className="px-5 py-4 border-b border-gray-200 flex items-start justify-between">
                <div>
                  <h3 className="text-lg font-semibold text-gray-900">{detailReport.client.name}</h3>
                  <a href={detailReport.result.url} target="_blank" rel="noopener noreferrer"
                     className="text-xs text-brand-600 hover:underline">
                    {detailReport.result.url}
                  </a>
                </div>
                <button onClick={() => setSelectedClientId(null)}
                        className="text-gray-400 hover:text-gray-600 text-2xl leading-none">
                  ×
                </button>
              </div>
              <div className="p-5">
                <BotMatrix result={detailReport.result} />
              </div>
            </div>
          </div>
        )}
      </div>
    </Layout>
  );
}

function StatCard({ label, value, loading, tone }: {
  label: string; value: number | string; loading?: boolean; tone?: 'warn' | 'muted';
}) {
  const toneCls =
    tone === 'warn' ? 'text-rose-700' : tone === 'muted' ? 'text-gray-500' : 'text-gray-900';
  return (
    <div className="bg-white border border-gray-200 rounded-lg p-4">
      <div className="text-xs text-gray-500 uppercase tracking-wider">{label}</div>
      <div className={`text-2xl font-bold mt-1 ${toneCls}`}>
        {loading ? '—' : value}
      </div>
    </div>
  );
}

function BotMatrix({ result }: { result: SiteResult }) {
  const allowed = result.results.filter((r) => r.status === 'allowed' || r.status === 'robots_only').length;
  const blocked = result.results.filter((r) => r.status === 'blocked' || r.status === 'partial').length;
  const inconclusive = result.results.filter((r) => r.status === 'inconclusive').length;
  const total = result.results.length;
  const proberBlocked = !!result.homepage?.prober_ip_blocked;
  return (
    <div>
      {proberBlocked && (
        <div className="mb-3 p-3 bg-amber-50 border border-amber-200 rounded text-xs text-amber-900">
          <div className="font-semibold mb-1">
            HTTP probe inconclusive — site returned HTTP {result.homepage?.status_code} to a normal browser too
            {result.homepage?.server?.includes('cloudflare') ? ' (Cloudflare)' : ''}.
          </div>
          <div className="opacity-80">
            This usually means the site is blocking our prober's IP at the WAF/CDN level — not that the bots
            themselves are blocked. The verdict below relies on robots.txt + homepage meta signals only;
            HTTP-level UA blocking can't be confirmed from this network.
          </div>
        </div>
      )}
      <div className="flex flex-wrap items-center gap-3 text-xs text-gray-600 mb-3">
        {result.homepage && (
          <span className={`px-2 py-0.5 rounded border ${
            result.homepage.ok
              ? 'bg-emerald-50 text-emerald-700 border-emerald-200'
              : proberBlocked
                ? 'bg-amber-50 text-amber-700 border-amber-200'
                : result.homepage.error
                  ? 'bg-amber-50 text-amber-700 border-amber-200'
                  : 'bg-rose-50 text-rose-700 border-rose-200'
          }`}>
            homepage: {result.homepage.ok
              ? `HTTP ${result.homepage.status_code} OK`
              : proberBlocked
                ? `HTTP ${result.homepage.status_code} (prober blocked)`
                : result.homepage.error
                  ? `unreachable — ${result.homepage.error}`
                  : `HTTP ${result.homepage.status_code ?? '?'}`}
          </span>
        )}
        <span className="px-2 py-0.5 bg-emerald-50 text-emerald-700 border border-emerald-200 rounded">
          {allowed}/{total} allowed
        </span>
        <span className={`px-2 py-0.5 rounded border ${blocked > 0 ? 'bg-rose-50 text-rose-700 border-rose-200' : 'bg-gray-50 text-gray-600 border-gray-200'}`}>
          {blocked}/{total} blocked
        </span>
        {inconclusive > 0 && (
          <span className="px-2 py-0.5 bg-amber-50 text-amber-700 border border-amber-200 rounded">
            {inconclusive}/{total} inconclusive
          </span>
        )}
        <span className="text-gray-500">
          robots.txt: {result.robots.fetched
            ? <>fetched — HTTP {result.robots.status ?? '?'}</>
            : <>could not fetch ({result.robots.error || 'unknown'})</>}
        </span>
      </div>
      <RobotsBlockedAgents agents={result.robots.blocked_agents} />
      {(result.homepage?.meta_robots && result.homepage.meta_robots.length > 0) || result.homepage?.x_robots_tag ? (
        <div className="mb-3 p-2 bg-gray-50 border border-gray-200 rounded text-xs text-gray-700 space-y-1">
          <div className="font-semibold text-gray-600">Homepage AI signals found:</div>
          {result.homepage?.meta_robots?.map((mr, i) => (
            <div key={i} className="font-mono">
              &lt;meta name="{mr.name}" content="{mr.content}"&gt;
            </div>
          ))}
          {result.homepage?.x_robots_tag && (
            <div className="font-mono">X-Robots-Tag: {result.homepage.x_robots_tag}</div>
          )}
        </div>
      ) : null}
      <table className="w-full text-sm">
        <thead className="bg-gray-50 border-b border-gray-200">
          <tr>
            <th className="text-left px-3 py-2 font-medium text-gray-600">Bot</th>
            <th className="text-left px-3 py-2 font-medium text-gray-600">Vendor</th>
            <th className="text-left px-3 py-2 font-medium text-gray-600">Robots</th>
            <th className="text-left px-3 py-2 font-medium text-gray-600">Meta</th>
            <th className="text-left px-3 py-2 font-medium text-gray-600">HTTP</th>
            <th className="text-left px-3 py-2 font-medium text-gray-600">Status</th>
            <th className="text-left px-3 py-2 font-medium text-gray-600">Evidence</th>
          </tr>
        </thead>
        <tbody>
          {result.results.map((r) => {
            const s = STATUS_STYLES[r.status];
            return (
              <tr key={r.id} className="border-b border-gray-100">
                <td className="px-3 py-2 font-medium text-gray-900">{r.name}</td>
                <td className="px-3 py-2 text-gray-600">{r.vendor}</td>
                <td className="px-3 py-2 text-xs text-gray-700">
                  {r.robots_blocked === true
                    ? <span className="text-rose-700">disallow</span>
                    : r.robots_blocked === false
                      ? <span className="text-emerald-700">allow</span>
                      : <span className="text-gray-400">unknown</span>}
                </td>
                <td className="px-3 py-2 text-xs text-gray-700">
                  {r.meta_blocked
                    ? <span className="text-rose-700" title={r.meta_reason || ''}>opt-out</span>
                    : <span className="text-emerald-700">none</span>}
                </td>
                <td className="px-3 py-2 text-xs text-gray-700">
                  {r.http_inconclusive
                    ? <span className="text-amber-700" title="Same response as a regular browser — prober IP is blocked, can't isolate UA-based blocking">{r.http_status ?? '?'} <span className="opacity-60">(?)</span></span>
                    : r.http_status ? `${r.http_status}${r.http_blocked ? ' ✕' : ''}`
                      : (r.error ? <span className="text-amber-700">err</span>
                                 : <span className="text-gray-400">—</span>)}
                </td>
                <td className="px-3 py-2">
                  <BlockReasonPopover impact={r.impact} reasons={r.block_reasons}>
                    <span className={`inline-flex items-center gap-1.5 px-2 py-0.5 rounded-full border text-xs font-medium ${s.cls}`}>
                      <span className={`w-1.5 h-1.5 rounded-full ${s.dot}`} />
                      {s.label}
                      {!!r.block_reasons?.length && <span className="opacity-60">ⓘ</span>}
                    </span>
                  </BlockReasonPopover>
                </td>
                <td className="px-3 py-2 text-xs text-gray-500 max-w-md">{r.evidence}</td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}
